import { currencyFormat } from 'legacy/common/utils/currency';
import { DiscountCodeDto, EventForUser, EventGuest } from './types';

const PERCENTAGE = 100;

export function applyDiscount(
  price: number,
  discount?: DiscountCodeDto
): number {
  if (!discount || !discount.discount) {
    return price;
  }

  switch (discount.discount.kind) {
    case 'percentage':
      return (price * (PERCENTAGE - discount.discount.value)) / PERCENTAGE;
    case 'amount':
      return Math.max(price - discount.discount.value, 0);
    default:
      return price;
  }
}

/**
 * Returns the price range of the event.
 */
export function getEventPricing(
  event: EventForUser,
  discount?: DiscountCodeDto
) {
  const publicTiers = event?.pricing?.tiers?.filter((tier) => !tier.private);

  if (!publicTiers?.length) {
    return 'Pricing varies';
  }

  const sortedTiersByPrice = [...publicTiers].sort((a, b) => a.price - b.price);
  const lowestPrice = sortedTiersByPrice[0].price || 0;
  const highestPrice = sortedTiersByPrice[sortedTiersByPrice.length - 1].price;

  if (lowestPrice === highestPrice && lowestPrice === 0) {
    return `Free`;
  }

  const discountedLowestPrice = applyDiscount(lowestPrice, discount);
  const discountedHighestPrice = applyDiscount(highestPrice, discount);

  if (discountedLowestPrice === discountedHighestPrice) {
    return currencyFormat(discountedLowestPrice);
  }

  return `${currencyFormat(discountedLowestPrice)} - ${currencyFormat(
    discountedHighestPrice
  )}`;
}

export function buildGuestsMessage(event: EventForUser, guests?: EventGuest[]) {
  const isUserAttending = event.userAttendanceStatus === 'attending';

  const friends = guests?.filter((g) => g.friendStatus === 'friends') || [];
  const nonFriendsLength =
    event.totalGuests - friends.length - (isUserAttending ? 1 : 0);

  const messageBuilder = [];

  if (isUserAttending) {
    messageBuilder.push('You');
  }

  // Take two friends only when user is not attending or (when there are no non-friends and two friends attending)
  const showFriendsCount =
    !isUserAttending || (nonFriendsLength === 0 && friends.length === 2)
      ? 2
      : 1;

  if (friends.length > 0) {
    friends
      .slice(0, showFriendsCount)
      .map((friend) => friend.firstName)
      .forEach((name) => {
        messageBuilder.push(name);
      });
  }

  const othersCount =
    nonFriendsLength + Math.max(0, friends.length - showFriendsCount);

  // If no friends, show message like "3 people are going"
  if (!isUserAttending && friends.length === 0 && nonFriendsLength > 0) {
    messageBuilder.push(
      `${othersCount} ${othersCount > 1 ? 'people' : 'person'}`
    );
  }

  // If user or any friends are attending, show message with "You and 3 others are going"
  if ((isUserAttending || friends.length > 0) && othersCount > 0) {
    messageBuilder.push(
      `${othersCount} ${othersCount > 1 ? 'others' : 'other'}`
    );
  }

  // If only the user is attending
  if (isUserAttending && messageBuilder.length === 1) {
    return `You are going`;
  }

  // If only one friend or one non friend is attending
  if (
    (friends.length === 1 || nonFriendsLength === 1) &&
    messageBuilder.length === 1
  ) {
    return `${messageBuilder[0]} is going`;
  }

  // If only non friends are attending
  if (messageBuilder.length === 1 && nonFriendsLength > 1) {
    return `${messageBuilder[0]} are going`;
  }

  if (messageBuilder.length > 1) {
    // Removes the last element and joins the rest with commas, and adds "and" for the last element
    return `${messageBuilder.slice(0, -1).join(', ')} and ${
      messageBuilder[messageBuilder.length - 1]
    } are going`;
  }

  return '';
}

export const validateEvent = (values) => {
  const errors: any = {};
  if (!values.title || values.title.trim().length === 0) {
    errors.title = 'Event Title is required to publish this event';
  }
  if (!values.startDate) {
    errors.startDate = 'Event Start Date is required to publish this event';
  }
  if (
    values.startDate &&
    values.endDate &&
    new Date(values.endDate) < new Date(values.startDate)
  ) {
    errors.endDate = 'End Date must be before Start Date to publish this event';
  }
  if (!values.address) {
    errors.address = 'Address is required to publish this event';
  }
  if (
    !values.photo ||
    !values.photo.url ||
    values.photo.url.trim().length === 0
  ) {
    errors.photo = 'Cover photo is required to publish this event';
  }
  if (!values.description) {
    errors.description = 'Event description is required to publish this event';
  }

  if (values.pricing?.tax) {
    const taxErrors = {};
    if (!values.pricing.tax.taxId || values.pricing.tax.taxId.length < 9) {
      Object.assign(taxErrors, {
        taxId: 'Tax ID must be at least 9 characters'
      });
    }
    if (
      !values.pricing.tax.name ||
      values.pricing.tax.name.length === 0 ||
      values.pricing.tax.name.length >= 20
    ) {
      Object.assign(taxErrors, {
        name: 'Tax Name must be specified, and cannot be longer than 20 characters'
      });
    }
    if (
      !values.pricing.tax.ratePercent ||
      values.pricing.tax.ratePercent <= 0 ||
      values.pricing.tax.ratePercent >= 50
    ) {
      Object.assign(taxErrors, {
        ratePercent:
          'Tax Rate percent must be specified, and must be greater than 0 and less than 50%'
      });
    }
    if (Object.keys(taxErrors).length > 0) {
      Object.assign(errors, { pricing: { tax: taxErrors } });
    }
  }
  return errors;
};

export const shouldEnableOrganizationFee = (groups) => {
  for (let i = 0; i < groups?.length; i++) {
    if (groups[i]?.canEnableEventOrganizerFee) {
      return true;
    }
  }

  return false;
};
